/**
 * HardwareTypeCards - the hardware type choice in the pour location dialog
 * (pour create wizard + pour edit page), one card per type with the same
 * product image the Hardware pages show.
 *
 * Also exports the image for each type, for the device header rows of the
 * location tables. A type whose image file is missing or fails to load shows
 * a plain chip icon instead of a broken picture.
 */
import React, { useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Memory as ChipIcon } from '@mui/icons-material';
import type { LocationHardwareType } from '../../utils/locationFunction';
import { BRAND_GREEN } from './pourWizardStyles';

const HARDWARE_TYPE_IMAGES: Record<Exclude<LocationHardwareType, ''>, string> = {
  ConXagent: 'conxagent.png',
  ConXsensors: 'conxsensor.png',
  ConXone: 'conxone.png',
  TMC: 'tank.png',
  'Weather Station': 'weather-station.png',
  'Temperature & Humidity': 'conxenviro.png',
};

/** The product image of a hardware type, or null when it has none. */
export const hardwareTypeImageUrl = (type: LocationHardwareType): string | null =>
  type ? `${process.env.PUBLIC_URL || ''}/hardware/${HARDWARE_TYPE_IMAGES[type]}` : null;

/** A hardware type's product image at `size` px, or the chip icon when the
 *  image is missing or does not load. */
const ProductImage: React.FC<{ type: LocationHardwareType; size: number }> = ({ type, size }) => {
  const url = hardwareTypeImageUrl(type);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (!url || failedUrl === url) {
    return <ChipIcon data-testid="hardware-image-fallback" sx={{ fontSize: size * 0.7, color: 'text.secondary' }} />;
  }
  return (
    <Box
      component="img"
      src={url}
      alt=""
      onError={() => setFailedUrl(url)}
      // An image with a white (not transparent) background, such as the
      // ConXone's, then takes the tile's colour instead of showing a box.
      sx={{ width: size, height: size, objectFit: 'contain', mixBlendMode: 'multiply' }}
    />
  );
};

/** One hardware type's image in a white rounded tile. */
export const HardwareTypeImage: React.FC<{ type: LocationHardwareType; size?: number }> = ({
  type,
  size = 40,
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: '8px',
      border: '1px solid',
      borderColor: 'grey.200',
      bgcolor: '#fff',
      display: 'grid',
      placeItems: 'center',
    }}
  >
    {type && <ProductImage type={type} size={size - 6} />}
  </Box>
);

export interface HardwareTypeCardsProps {
  options: { value: LocationHardwareType; label: string }[];
  value: LocationHardwareType;
  onChange: (value: LocationHardwareType) => void;
  /** Editing: the type is fixed, the cards only show it. */
  disabled?: boolean;
}

const HardwareTypeCards: React.FC<HardwareTypeCardsProps> = ({ options, value, onChange, disabled }) => (
  <Box
    role="radiogroup"
    aria-label="Hardware type"
    sx={{
      display: 'grid',
      // Every type on one row; three a row on a phone.
      gridTemplateColumns: {
        xs: 'repeat(3, minmax(0, 1fr))',
        sm: `repeat(${Math.max(options.length, 1)}, minmax(0, 1fr))`,
      },
      gap: 0.75,
    }}
  >
    {options.map((opt) => {
      const selected = opt.value === value;
      return (
        <ButtonBase
          key={opt.value}
          role="radio"
          aria-checked={selected}
          aria-label={opt.label.replace('\n', ' ')}
          disabled={disabled && !selected}
          onClick={() => !disabled && onChange(opt.value)}
          sx={{
            flexDirection: 'column',
            gap: 0.5,
            px: 0.5,
            py: 0.75,
            minWidth: 0,
            borderRadius: '10px',
            border: '1.5px solid',
            borderColor: selected ? BRAND_GREEN : 'grey.300',
            bgcolor: selected ? 'rgba(154, 208, 46, 0.1)' : '#fff',
            opacity: disabled && !selected ? 0.4 : 1,
            transition: 'border-color 0.15s, background-color 0.15s',
            '&:hover': { borderColor: BRAND_GREEN },
          }}
        >
          <Box sx={{ width: 40, height: 40, display: 'grid', placeItems: 'center' }}>
            <ProductImage type={opt.value} size={40} />
          </Box>
          <Typography
            variant="caption"
            align="center"
            sx={{ fontSize: '0.64rem', lineHeight: 1.2, fontWeight: selected ? 700 : 500, whiteSpace: 'pre-line' }}
          >
            {opt.label}
          </Typography>
        </ButtonBase>
      );
    })}
  </Box>
);

export default HardwareTypeCards;
